import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@scio3/ui';
import { GET_UPCOMING_AND_UNPAID_SUBSCRIPTION } from '@scio3/graphql-client';
import { useCompanyFeatures } from '@/hooks/useCompanyFeatures';

type UnpaidInvoicesAlertProps = {
  subAccountId?: string;
  teamId?: string;
};

/**
 * Warning banner shown when the account has unpaid invoices.
 *
 * scio1 parity (#1160, subscriptionPlanModal.html:1136-1141): scio1 has only
 * one button — "Update Payment Method" — that navigates to the Payment
 * Settings page. The previous scio3 "Pay Now" button attempted to re-charge
 * the same card on file, which keeps failing for accounts with insufficient
 * funds (the most common cause of unpaid invoices). The navigation gives the
 * user a clear path to update their card and resolve the failure.
 *
 * Suppressed for enterprise accounts with feature.disablePaymentCheck
 * (SCIO 1.0 parity: subscriptionPlanModal.js:1956).
 */
export function UnpaidInvoicesAlert({ subAccountId, teamId }: UnpaidInvoicesAlertProps) {
  const { t } = useTranslation();
  const { disablePaymentCheck } = useCompanyFeatures();
  const { data, loading: queryLoading } = useQuery(GET_UPCOMING_AND_UNPAID_SUBSCRIPTION, {
    variables: { subAccountId, teamId },
    fetchPolicy: 'network-only',
    skip: disablePaymentCheck,
  });

  const hasUnpaid =
    (data as Record<string, { status?: boolean } | undefined> | undefined)
      ?.getUpcomingAndUnpaidSubscription?.status === false;

  // Wait for API to finish before deciding to show — prevents flash
  if (disablePaymentCheck || queryLoading || !hasUnpaid) return null;

  const handleUpdatePayment = () => {
    window.location.href = '/app/s/payment';
  };

  return (
    <div
      className="flex items-center gap-3 rounded-lg border border-warning bg-warning/10 p-4"
      data-testid="unpaid-invoices-alert"
    >
      <AlertTriangle className="h-5 w-5 shrink-0 text-warning" />
      <div className="flex-1 text-sm">
        <p className="font-medium">{t('You have unpaid invoices')}</p>
        <p className="text-muted-foreground">
          {t('Please update your payment method or pay outstanding invoices.')}
        </p>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={handleUpdatePayment}
        className="cursor-pointer shrink-0"
        data-testid="unpaid-invoices-update-payment-btn"
      >
        {t('Update Payment Method')}
      </Button>
    </div>
  );
}
